<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import { useForwardPropsEmits } from 'reka-ui'
import {
  Dialog as DialogRoot,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

import type { DialogContentEmits, DialogContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'

type Props = DialogContentProps & {
  contentClass?: HTMLAttributes['class']
  // Screen-reader-only summary of the dialog, announced when it opens
  description?: string
  showCloseButton?: boolean
  // False hides the close button and blocks escape/outside-click dismissal
  dismissible?: boolean
  // Lays the dialog out as a non-scrolling flex column: the body owns its own
  // scrolling (via min-h-0 flex-1) so header and footer stay visible
  fixedHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showCloseButton: true,
  dismissible: true,
  fixedHeight: false
})

const emits = defineEmits<DialogContentEmits>()

const visible = defineModel<boolean>({ default: false })

// Drop the wrapper-only props (unknown to DialogContent), keeping the rest reactive
const delegatedProps = reactiveOmit(props, 'contentClass', 'description', 'showCloseButton', 'dismissible', 'fixedHeight')

// Adds onX re-emit listeners for our emits, so one v-bind forwards props down
// and relays DialogContent's events back up (they don't bubble on their own)
const forwarded = useForwardPropsEmits(delegatedProps, emits)

// Explicit listeners replace the generated ones in `forwarded`, so re-emit manually
const onEscapeKeyDown = (event: DialogContentEmits['escapeKeyDown'][0]) => {
  if (!props.dismissible) event.preventDefault()
  emits('escapeKeyDown', event)
}

const onPointerDownOutside = (event: DialogContentEmits['pointerDownOutside'][0]) => {
  if (!props.dismissible) event.preventDefault()
  emits('pointerDownOutside', event)
}

const onInteractOutside = (event: DialogContentEmits['interactOutside'][0]) => {
  if (!props.dismissible) event.preventDefault()
  emits('interactOutside', event)
}
</script>

<template>
  <DialogRoot v-model:open="visible">
    <DialogContent
      v-bind="forwarded"
      :class="[props.fixedHeight ? 'flex max-h-[80vh] flex-col overflow-hidden' : 'max-h-[80vh] overflow-y-auto', props.contentClass]"
      :show-close-button="props.showCloseButton && props.dismissible"
      @escape-key-down="onEscapeKeyDown"
      @pointer-down-outside="onPointerDownOutside"
      @interact-outside="onInteractOutside"
    >
      <DialogHeader>
        <DialogTitle>
          <slot name="header" />
        </DialogTitle>
        <DialogDescription class="sr-only">{{ props.description ?? '' }}</DialogDescription>
      </DialogHeader>

      <slot />

      <DialogFooter v-if="$slots.footer">
        <slot name="footer" />
      </DialogFooter>
    </DialogContent>
  </DialogRoot>
</template>
